import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';

const EVENT_FIELDS = [
  {
    key: 'title',
    label: 'Event Title',
    required: true,
    aliases: ['title', 'event title', 'name', 'event name', 'subject', 'summary'],
  },
  {
    key: 'kind',
    label: 'Event Type',
    required: true,
    aliases: ['type', 'kind', 'event type', 'category'],
    helper: 'CUSTOM, EARLY_RELEASE, LATE_START, or NO_SCHOOL',
  },
  {
    key: 'start_date',
    label: 'Start Date',
    required: true,
    aliases: ['date', 'start date', 'start', 'event date', 'from', 'begin date'],
  },
  {
    key: 'end_date',
    label: 'End Date',
    aliases: ['end date', 'end', 'to', 'finish date', 'until'],
    helper: 'Optional — defaults to start date',
  },
  {
    key: 'dismissal_time',
    label: 'Event Time',
    aliases: ['event time', 'start time', 'dismissal time', 'time', 'release time', 'pickup time', 'end time'],
    helper: 'Required for early release and late start; optional for school events',
  },
  {
    key: 'grade',
    label: 'Grade',
    aliases: ['grade', 'grade level'],
    defaultValue: 'ALL',
    helper: 'Default: ALL',
  },
  {
    key: 'gender',
    label: 'Gender',
    aliases: ['gender', 'sex'],
    helper: 'Optional — M or F',
  },
  {
    key: 'notes',
    label: 'Notes',
    aliases: ['notes', 'description', 'details', 'comment', 'comments'],
    helper: 'Optional additional details',
  },
];

const KIND_OPTIONS = ['CUSTOM', 'EARLY_RELEASE', 'LATE_START', 'NO_SCHOOL'];

const REQUIRED_STYLE = {
  color: '#b91c1c',
  fontSize: 11,
  fontWeight: 700,
  marginLeft: 6,
};

function normalizeHeader(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function parseCsv(text) {
  const rows = [];
  let current = [];
  let value = '';
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        value += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      current.push(value);
      value = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') index += 1;
      current.push(value);
      if (current.some((cell) => String(cell).trim() !== '')) {
        rows.push(current);
      }
      current = [];
      value = '';
      continue;
    }

    value += char;
  }

  current.push(value);
  if (current.some((cell) => String(cell).trim() !== '')) {
    rows.push(current);
  }

  if (rows.length === 0) return { headers: [], records: [] };

  const headers = rows[0].map((header, index) => (
    String(header || `Column ${index + 1}`).replace(/^﻿/, '').trim() || `Column ${index + 1}`
  ));
  const records = rows.slice(1).map((row, rowIndex) => {
    const record = { __rowNumber: rowIndex + 2 };
    headers.forEach((header, index) => {
      record[header] = String(row[index] || '').trim();
    });
    return record;
  });

  return { headers, records };
}

function guessColumn(field, headers) {
  const candidates = [field.label, field.key, ...(field.aliases || [])].map(normalizeHeader);
  const normalizedHeaders = headers.map((header) => ({ header, normalized: normalizeHeader(header) }));
  const exact = normalizedHeaders.find((item) => candidates.includes(item.normalized));
  if (exact) return exact.header;

  const loose = normalizedHeaders.find((item) => (
    candidates.some((candidate) => candidate && (item.normalized.includes(candidate) || candidate.includes(item.normalized)))
  ));
  return loose?.header || '';
}

function buildInitialMappings(headers) {
  return Object.fromEntries(EVENT_FIELDS.map((field) => [
    field.key,
    {
      column: guessColumn(field, headers),
      manual: field.defaultValue || '',
    },
  ]));
}

function getMappedValue(row, mapping) {
  const csvValue = mapping?.column ? String(row[mapping.column] || '').trim() : '';
  const manualValue = String(mapping?.manual || '').trim();
  return csvValue || manualValue;
}

function normalizeDateValue(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const slashMatch = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (slashMatch) {
    const month = slashMatch[1].padStart(2, '0');
    const day = slashMatch[2].padStart(2, '0');
    const year = slashMatch[3].length === 2 ? `20${slashMatch[3]}` : slashMatch[3];
    return `${year}-${month}-${day}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return [
      parsed.getFullYear(),
      String(parsed.getMonth() + 1).padStart(2, '0'),
      String(parsed.getDate()).padStart(2, '0'),
    ].join('-');
  }

  return '';
}

function normalizeTimeValue(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const ampmMatch = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (ampmMatch) {
    let hour = Number(ampmMatch[1]);
    const minute = ampmMatch[2] ? Number(ampmMatch[2]) : 0;
    const meridiem = ampmMatch[3].toLowerCase();
    if (meridiem === 'pm' && hour < 12) hour += 12;
    if (meridiem === 'am' && hour === 12) hour = 0;
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
  }

  const hmsMatch = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (hmsMatch) {
    const hour = String(Number(hmsMatch[1])).padStart(2, '0');
    const minute = hmsMatch[2];
    const second = hmsMatch[3] || '00';
    return `${hour}:${minute}:${second}`;
  }

  return '';
}

function normalizeKindValue(value) {
  const raw = String(value || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (!raw) return 'CUSTOM';
  if (KIND_OPTIONS.includes(raw)) return raw;
  if (['NOSCHOOL', 'NO_SCHOOL_DAY', 'HOLIDAY', 'CLOSED'].includes(raw)) return 'NO_SCHOOL';
  if (['EARLY', 'EARLY_DISMISSAL', 'EARLY_OUT', 'EARLY_RELEASE_DAY'].includes(raw)) return 'EARLY_RELEASE';
  if (['LATE', 'LATE_ARRIVAL', 'LATE_OPEN', 'DELAYED_START'].includes(raw)) return 'LATE_START';
  if (['EVENT', 'SCHOOL_EVENT', 'SCHOOLEVENT'].includes(raw)) return 'CUSTOM';
  return raw;
}

function normalizeGenderValue(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return null;
  if (['m', 'male', 'boy'].includes(raw)) return 'M';
  if (['f', 'female', 'girl'].includes(raw)) return 'F';
  return null;
}

function buildPayload(schoolId, row, mappings) {
  const title = getMappedValue(row, mappings.title);
  const kind = normalizeKindValue(getMappedValue(row, mappings.kind) || 'CUSTOM');
  const startDate = normalizeDateValue(getMappedValue(row, mappings.start_date));
  const endDate = normalizeDateValue(getMappedValue(row, mappings.end_date)) || startDate;
  const dismissalTime = normalizeTimeValue(getMappedValue(row, mappings.dismissal_time));
  const grade = getMappedValue(row, mappings.grade) || 'ALL';
  const gender = normalizeGenderValue(getMappedValue(row, mappings.gender));
  const extraNotes = getMappedValue(row, mappings.notes);
  const noteBody = [title, extraNotes].filter(Boolean).join('\n');

  const startTime = ['EARLY_RELEASE', 'LATE_START', 'CUSTOM'].includes(kind)
    ? (dismissalTime || null)
    : null;
  const endTime = ['EARLY_RELEASE', 'CUSTOM'].includes(kind)
    ? (dismissalTime || null)
    : null;

  return {
    school_id: Number(schoolId),
    start_date: startDate,
    end_date: endDate,
    grade,
    gender,
    kind,
    notes: noteBody,
    start_time: startTime,
    end_time: endTime,
  };
}

function getRowIssues(row, mappings) {
  const issues = [];

  EVENT_FIELDS.forEach((field) => {
    if (!field.required) return;
    if (!getMappedValue(row, mappings[field.key] || {})) {
      issues.push(`Missing ${field.label}`);
    }
  });

  const startDateRaw = getMappedValue(row, mappings.start_date);
  if (startDateRaw && !normalizeDateValue(startDateRaw)) {
    issues.push('Invalid Start Date');
  }

  const endDateRaw = getMappedValue(row, mappings.end_date);
  if (endDateRaw && !normalizeDateValue(endDateRaw)) {
    issues.push('Invalid End Date');
  }

  const kindRaw = getMappedValue(row, mappings.kind);
  const kind = normalizeKindValue(kindRaw || 'CUSTOM');
  if (!KIND_OPTIONS.includes(kind)) {
    issues.push('Invalid Event Type');
  }

  const timeRaw = getMappedValue(row, mappings.dismissal_time);
  if (['EARLY_RELEASE', 'LATE_START'].includes(kind) && !timeRaw) {
    issues.push('Missing Event Time');
  } else if (timeRaw && !normalizeTimeValue(timeRaw)) {
    issues.push('Invalid Event Time');
  }

  return issues;
}

export default function CalendarCsvImportModal({ token, schoolId, onClose, onImported }) {
  const [headers, setHeaders] = useState([]);
  const [records, setRecords] = useState([]);
  const [mappings, setMappings] = useState(() => buildInitialMappings([]));
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    setMappings(buildInitialMappings(headers));
    setResults([]);
  }, [headers]);

  const validation = useMemo(() => {
    const missingMappings = EVENT_FIELDS
      .filter((field) => field.required)
      .filter((field) => {
        const mapping = mappings[field.key] || {};
        return !mapping.column && !String(mapping.manual || '').trim();
      });

    const rowIssues = records
      .map((row) => ({ rowNumber: row.__rowNumber, issues: getRowIssues(row, mappings) }))
      .filter((item) => item.issues.length > 0);

    return { missingMappings, rowIssues };
  }, [mappings, records]);

  const canImport = records.length > 0
    && validation.missingMappings.length === 0
    && validation.rowIssues.length === 0
    && !importing;

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setError('');
    setResults([]);
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.headers.length === 0 || parsed.records.length === 0) {
        setError('CSV must include a header row and at least one data row.');
        setHeaders([]);
        setRecords([]);
        setFileName('');
        return;
      }

      setHeaders(parsed.headers);
      setRecords(parsed.records);
      setFileName(file.name);
    } catch (err) {
      setError(err.message || 'Could not parse CSV file.');
    }
  };

  const updateMapping = (fieldKey, patch) => {
    setMappings((current) => ({
      ...current,
      [fieldKey]: {
        ...(current[fieldKey] || { column: '', manual: '' }),
        ...patch,
      },
    }));
  };

  const handleImport = async () => {
    if (!canImport) return;

    setImporting(true);
    setError('');
    setResults([]);

    const nextResults = [];

    for (const row of records) {
      const payload = buildPayload(schoolId, row, mappings);
      try {
        await apiFetch('/dismissal-events', {
          method: 'POST',
          body: JSON.stringify(payload),
        }, token);
        nextResults.push({ rowNumber: row.__rowNumber, success: true, message: 'Imported' });
      } catch (err) {
        nextResults.push({ rowNumber: row.__rowNumber, success: false, message: err.message });
      }
      setResults([...nextResults]);
    }

    setImporting(false);
    if (nextResults.some((result) => result.success)) {
      onImported?.();
    }
  };

  const previewRows = records.slice(0, 5);
  const importedCount = results.filter((result) => result.success).length;
  const failedCount = results.filter((result) => !result.success).length;

  return (
    <div className="sd-support-overlay" onClick={onClose}>
      <div
        className="sd-support-modal sca-csv-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="sca-csv-modal-header">
          <div>
            <h2>Import Events from CSV</h2>
            <p>Upload a CSV file and map columns to calendar event fields.</p>
          </div>
          <button type="button" className="sca-csv-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {error ? <div className="sa-error" style={{ marginBottom: 12 }}>{error}</div> : null}

        <div className="sca-csv-file-row">
          <label className="sca-csv-file-label">
            <span>CSV File</span>
            <input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
          </label>
          {fileName ? (
            <span className="sca-csv-file-meta">
              {fileName} · {records.length} row{records.length === 1 ? '' : 's'} · {headers.length} column{headers.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>

        {headers.length > 0 ? (
          <div className="sca-csv-section">
            <h3>Map Fields</h3>
            <div className="sca-csv-table-wrap">
              <table className="sca-csv-table">
                <thead>
                  <tr>
                    <th>Event Field</th>
                    <th>CSV Column</th>
                    <th>Manual / Default</th>
                  </tr>
                </thead>
                <tbody>
                  {EVENT_FIELDS.map((field) => {
                    const mapping = mappings[field.key] || { column: '', manual: '' };
                    return (
                      <tr key={field.key}>
                        <td>
                          <div className="sca-csv-field-label">
                            {field.label}
                            {field.required ? <span style={REQUIRED_STYLE}>Required</span> : null}
                          </div>
                          {field.helper ? <div className="sca-csv-field-helper">{field.helper}</div> : null}
                        </td>
                        <td>
                          <select
                            value={mapping.column}
                            onChange={(event) => updateMapping(field.key, { column: event.target.value })}
                          >
                            <option value="">Not mapped</option>
                            {headers.map((header) => (
                              <option key={header} value={header}>{header}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            value={mapping.manual}
                            onChange={(event) => updateMapping(field.key, { manual: event.target.value })}
                            placeholder={field.required ? 'Required if column blank' : 'Optional fallback'}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {validation.missingMappings.length > 0 ? (
              <div className="sa-error" style={{ marginTop: 10 }}>
                Required fields need a CSV column or manual value: {validation.missingMappings.map((field) => field.label).join(', ')}
              </div>
            ) : null}

            {validation.rowIssues.length > 0 ? (
              <div className="sa-error" style={{ marginTop: 10 }}>
                {validation.rowIssues.length} row{validation.rowIssues.length === 1 ? '' : 's'} have issues.
                First: row {validation.rowIssues[0].rowNumber} — {validation.rowIssues[0].issues.join(', ')}.
              </div>
            ) : null}
          </div>
        ) : null}

        {previewRows.length > 0 ? (
          <div className="sca-csv-section">
            <h3>Preview</h3>
            <div className="sca-csv-table-wrap">
              <table className="sca-csv-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row) => {
                    const issues = getRowIssues(row, mappings);
                    const title = getMappedValue(row, mappings.title);
                    const kind = normalizeKindValue(getMappedValue(row, mappings.kind) || 'CUSTOM');
                    const startDate = normalizeDateValue(getMappedValue(row, mappings.start_date));
                    const time = normalizeTimeValue(getMappedValue(row, mappings.dismissal_time));
                    return (
                      <tr key={row.__rowNumber}>
                        <td>{row.__rowNumber}</td>
                        <td>{title || '—'}</td>
                        <td>{kind}</td>
                        <td>{startDate || '—'}</td>
                        <td>{time || '—'}</td>
                        <td>
                          {issues.length === 0 ? (
                            <span className="sa-badge sa-badge-active">Ready</span>
                          ) : (
                            <span className="sa-badge sa-badge-suspended" title={issues.join(', ')}>
                              {issues[0]}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {records.length > previewRows.length ? (
              <div className="sca-csv-preview-note">
                Showing first {previewRows.length} of {records.length} rows.
              </div>
            ) : null}
          </div>
        ) : null}

        {results.length > 0 ? (
          <div className="sca-csv-section">
            <h3>Results</h3>
            <p className="sca-csv-result-summary">
              Imported {importedCount}. Failed {failedCount}.
            </p>
            {failedCount > 0 ? (
              <div className="sca-csv-table-wrap" style={{ maxHeight: 200 }}>
                <table className="sca-csv-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Result</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result) => (
                      <tr key={result.rowNumber}>
                        <td>{result.rowNumber}</td>
                        <td>
                          <span className={`sa-badge ${result.success ? 'sa-badge-active' : 'sa-badge-suspended'}`}>
                            {result.success ? 'Imported' : 'Failed'}
                          </span>
                        </td>
                        <td>{result.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="sca-csv-actions">
          <button
            type="button"
            className="sa-btn sa-btn-primary"
            onClick={handleImport}
            disabled={!canImport}
          >
            {importing ? 'Importing...' : `Import ${records.length || ''} Event${records.length === 1 ? '' : 's'}`.trim()}
          </button>
          <button type="button" className="sa-btn sa-btn-secondary" onClick={onClose} disabled={importing}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
